import { GraphyAdminEndpoints } from '@/constants/urls';
import {
  toActivateRequestBody,
  toSaveDraftRequestBody,
} from '@/routes/Agency/FirstResponder/Live/liveWireAdapter';
import type {
  FirstResponderAttentionWire,
  FirstResponderConsoleWire,
  FirstResponderJobsWire,
  FirstResponderJobWire,
  FirstResponderPolicyAuditsWire,
  FirstResponderPolicyWire,
  FirstResponderWorkersWire,
  WireMode,
  WireModeState,
  WireSafetyStatus,
} from '@/routes/Agency/FirstResponder/Live/liveWireContract';
import type { LivePilotPolicy } from '@/routes/Agency/FirstResponder/LivePreview/liveViewModel';
import type { FirstResponderTickResult } from '@/types/firstResponder';
import { axiosRubyClient as api } from '@/util/axios/axiosClient';

/*
 * First Responder policy admin console (P3-E). Separate read/write surface from
 * `firstResponderAPI.ts` (the legacy shadow-pilot command centre, untouched) — mode/safety
 * overview, policy read/draft/activate, paginated audit history. This service never generates an
 * Idempotency-Key; `saveDraft`/`activate` only forward the key the caller (Connected/index.tsx)
 * already generated — see that file for the sole-owner rationale.
 */

// Same dual error shape as firstResponderAPI/experimentsAPI: BaseController auth failures return
// { error: '…' } while this endpoint family's own actions return { errors: [...] }. Returns
// { message, errorCode } rather than a bare string — error_code is a distinct piece of
// information from both the message and from SubmitResult's own `code` (the HTTP status, read by
// the caller from err.response.status, never derived here).
export const extractPolicyApiError = (
  err: any,
  fallback: string,
): { message: string; errorCode: string | null } => ({
  message:
    err?.response?.data?.errors?.join(', ') ||
    err?.response?.data?.error ||
    err?.message ||
    fallback,
  errorCode: err?.response?.data?.error_code ?? null,
});

export const firstResponderConsoleAPI = {
  getConsole: async (): Promise<FirstResponderConsoleWire> => {
    const response = await api.get(
      GraphyAdminEndpoints.FIRST_RESPONDER_CONSOLE,
    );
    return response.data.data;
  },

  getPolicy: async (): Promise<FirstResponderPolicyWire> => {
    const response = await api.get(
      GraphyAdminEndpoints.FIRST_RESPONDER_CONSOLE_POLICY,
    );
    return response.data.data;
  },

  getAudits: async (
    page: number,
    perPage: number,
  ): Promise<FirstResponderPolicyAuditsWire> => {
    const response = await api.get(
      GraphyAdminEndpoints.FIRST_RESPONDER_CONSOLE_POLICY_AUDITS,
      { params: { page, per_page: perPage } },
    );
    return response.data.data;
  },

  saveDraft: async ({
    policy,
    basedOnVersion,
    expectedDraftVersion,
    reason,
    idempotencyKey,
  }: {
    policy: LivePilotPolicy;
    basedOnVersion: string;
    expectedDraftVersion: string | null;
    reason: string;
    idempotencyKey: string;
  }): Promise<FirstResponderPolicyWire> => {
    const body = toSaveDraftRequestBody(
      policy,
      basedOnVersion,
      expectedDraftVersion,
      reason,
    );
    const response = await api.patch(
      GraphyAdminEndpoints.FIRST_RESPONDER_CONSOLE_POLICY_DRAFT,
      body,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    return response.data.data;
  },

  activate: async ({
    basedOnVersion,
    expectedDraftVersion,
    reason,
    idempotencyKey,
  }: {
    basedOnVersion: string;
    expectedDraftVersion: string | null;
    reason: string;
    idempotencyKey: string;
  }): Promise<FirstResponderPolicyWire> => {
    const body = toActivateRequestBody(
      basedOnVersion,
      expectedDraftVersion,
      reason,
    );
    const response = await api.post(
      GraphyAdminEndpoints.FIRST_RESPONDER_CONSOLE_POLICY_ACTIVATE,
      body,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    return response.data.data;
  },

  // GRAVY-10373 Task 7: the four endpoints that retire the legacy overview adapter for this page.
  // customer_id is OMITTED entirely (never sent as literal null) when no client filter is
  // selected — the query param table in the brief reads "customer_id? (omit for all)".
  getJobs: async (
    customerId: number | null,
    page: number,
    perPage: number,
  ): Promise<FirstResponderJobsWire> => {
    const params: { page: number; per_page: number; customer_id?: number } = {
      page,
      per_page: perPage,
    };
    if (customerId !== null) params.customer_id = customerId;
    const response = await api.get(
      GraphyAdminEndpoints.FIRST_RESPONDER_CONSOLE_JOBS,
      { params },
    );
    return response.data.data;
  },

  // jobId is the BUSINESS job id (WireJobRow.job_id), never the run id (WireJobRow.id) — the
  // detail route is keyed by job id (see the brief's endpoint table).
  getJob: async (
    jobId: string,
    runId?: string | null,
  ): Promise<FirstResponderJobWire> => {
    // `run_id` names the exact evaluation; without it the server answers with the newest run, and
    // the request is exactly the one this endpoint has always received.
    const url = GraphyAdminEndpoints.FIRST_RESPONDER_CONSOLE_JOB_BY_ID(jobId);
    const response = runId
      ? await api.get(url, { params: { run_id: runId } })
      : await api.get(url);
    return response.data.data;
  },

  getWorkers: async (
    jobId: string,
    page: number,
    perPage: number,
    runId?: string | null,
  ): Promise<FirstResponderWorkersWire> => {
    const response = await api.get(
      GraphyAdminEndpoints.FIRST_RESPONDER_CONSOLE_JOB_WORKERS(jobId),
      {
        params: {
          page,
          per_page: perPage,
          ...(runId ? { run_id: runId } : {}),
        },
      },
    );
    return response.data.data;
  },

  getAttention: async (
    page: number,
    perPage: number,
  ): Promise<FirstResponderAttentionWire> => {
    const response = await api.get(
      GraphyAdminEndpoints.FIRST_RESPONDER_CONSOLE_ATTENTION,
      { params: { page, per_page: perPage } },
    );
    return response.data.data;
  },

  // GRAVY-10373 Task 3: the real OFF/SHADOW/LIVE mode transition. Every transition — including
  // Stop (-> off) — requires a non-blank reason; the backend refuses a blank one with
  // error_code 'reason_required'. No Idempotency-Key: unlike saveDraft/activate this isn't a
  // resource write with a stale-version conflict to dedupe against, it's a single mode command.
  setMode: async ({
    mode,
    reason,
  }: {
    mode: WireMode;
    reason: string;
  }): Promise<{ mode: WireModeState; safety: WireSafetyStatus }> => {
    const response = await api.post(
      GraphyAdminEndpoints.FIRST_RESPONDER_CONSOLE_MODE,
      { mode, reason },
    );
    return response.data.data;
  },

  // F11 (review-app testability): runs one tick of the CONFIGURED mode now, for a review app
  // that has no scheduler. Same audited endpoint the legacy firstResponderAPI.runShadowEvaluation
  // hits and the same rule: no body — the mode is read from the two feature flags server-side,
  // never named by the caller (see api/admin/first_responder_controller.rb#tick's own comment).
  runTick: async (): Promise<FirstResponderTickResult> => {
    const response = await api.post(GraphyAdminEndpoints.FIRST_RESPONDER_TICK);
    return response.data.data;
  },
};
